"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Cpu, Pipette, Layers, Workflow, CircleCheck as CheckCircle2, ArrowRight, Settings, SlidersHorizontal, ShieldCheck, Gauge, Network } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
interface AccessoryCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  badge: string;
  image: string;
  imageSrcset: string;
  imageSizes: string;
  specs: string;
}
const accessories: AccessoryCategory[] = [{
  id: "probes",
  name: "Immersion & Transmission Probes",
  icon: "pipette",
  description: "High-precision optical immersion and diffuse reflectance probes engineered for harsh chemical environments and high-pressure process vessels.",
  badge: "PAT Compliant",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/immersion_probes.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/immersion_probes.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/immersion_probes.png-wvc-sizes",
  specs: "Hastelloy C-276 / Sapphire optics, up to 250 bar & 300°C rating"
}, {
  id: "flow-cells",
  name: "Flow-Through & In-Line Cells",
  icon: "workflow",
  description: "Sanitary flow cells designed for continuous monitoring of liquids, suspensions, and chromatographic effluents with minimal dead volume.",
  badge: "Hygienic Tri-Clamp",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/flow_cells.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/flow_cells.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/flow_cells.png-wvc-sizes",
  specs: "316L SS electropolished, zero dead-leg geometry, CIP/SIP compatible"
}, {
  id: "autosamplers",
  name: "Automated Sample Handlers",
  icon: "cpu",
  description: "Multivessel sampling automation for benchtop dissolution, synthesis monitoring, and automated dilution protocols.",
  badge: "High Throughput",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/autosamplers.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/autosamplers.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/autosamplers.png-wvc-sizes",
  specs: "Up to 96 positions, Peltier thermal control, barcode verification"
}, {
  id: "adapters",
  name: "Process Interface Adapters",
  icon: "layers",
  description: "Standardized flange, Ingold, and Swagelok adapters for retrofitting PAT instruments onto bioreactors and production lines.",
  badge: "Standard Fitting",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_adapters.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_adapters.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_adapters.png-wvc-sizes",
  specs: "Ingold 25mm, BioConnect, ANSI flanges with laser-etched serials"
}];
interface IntegrationFeature {
  title: string;
  desc: string;
  tag: string;
}
const integrationFeatures: IntegrationFeature[] = [{
  title: "OPC UA & DCS Direct Connectivity",
  desc: "Direct fieldbus telemetry connecting spectroscopy and testing data straight to DeltaV, Siemens PCS 7, and Rockwell factory controllers.",
  tag: "Industry 4.0"
}, {
  title: "Data Integrity & Access Controls",
  desc: "Complete audit trails, cryptographic checksums, and role-based permissions integrated natively into laboratory LIMS and MES systems.",
  tag: "Data Integrity"
}, {
  title: "Real-Time Spectral Triggering",
  desc: "Low-latency hardware triggers synchronized with blender revolutions, tablet press strokes, and fluidic injection valves.",
  tag: "Precision Timing"
}, {
  title: "Plug-and-Play Quick Couplers",
  desc: "Fibre-optic and pneumatic quick-release couplings for seamless swapping between benchtop testing and pilot skid operations.",
  tag: "Modular Architecture"
}];
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const faqList: FaqItem[] = [{
  id: "item-1",
  question: "How do custom optical probes integrate with existing reactor ports?",
  answer: "Our engineers supply certified Ingold, Tri-Clamp, or ANSI flanged housing kits that seal directly into standard process ports with full material batch traceability and pressure certificates."
}, {
  id: "item-2",
  question: "Can these sampling accessories withstand aggressive CIP/SIP cycles?",
  answer: "Yes. All process-wetted surfaces utilize electropolished 316L stainless steel, Hastelloy C-276, and chemically brazed sapphire or diamond windows rated for repeated steam sterilization up to 150°C and caustic washdowns."
}, {
  id: "item-3",
  question: "Do you provide qualification protocols for integrated systems?",
  answer: "Antech Solutions delivers full IQ/OQ/PQ documentation packages, traceable calibration filters, and validation execution support across regulated sites in Ireland and the UK."
}];
export default function AccessoriesIntegration() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<string>("probes");
  return <section id="samplingaccessoriesintegration" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold tracking-wide uppercase">
              Modular Architecture
            </Badge>
            <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <ShieldCheck className="size-3.5 text-primary" />
              cGMP &amp; PAT Ready
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Sampling Accessories &amp; Integration Solutions
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Configure your analytical instruments for real-time monitoring across laboratory, pilot scale, and commercial manufacturing skids with precision-engineered sampling interfaces.
          </p>
        </div>

        {/* Tabbed Accessories Showcase */}
        <div className="mb-20">
          <Tabs defaultValue="probes" value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
              <TabsList className="h-auto p-1.5 bg-muted/80 flex flex-wrap max-w-full justify-start rounded-lg border border-border">
                {accessories.map((item, i) => <TabsTrigger key={item.id} value={item.id} data-index={i} className="px-4 py-2 text-sm font-medium transition-colors data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm rounded-md">
                    {item.name}
                  </TabsTrigger>)}
              </TabsList>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <SlidersHorizontal className="size-4 text-primary" />
                <span>Custom configurations available on request</span>
              </div>
            </div>

            {accessories.map((item, i) => <TabsContent key={item.id} value={item.id} data-index={i} className="focus-visible:outline-none">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-card border border-border rounded-xl p-6 sm:p-8 shadow-sm">
                  <div className="lg:col-span-6 space-y-6">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="border-primary/40 text-primary">
                          {item.badge}
                        </Badge>
                        <span className="text-xs text-muted-foreground font-mono">
                          ID: ANT-{item.id.toUpperCase()}
                        </span>
                      </div>
                      <h3 className="text-2xl sm:text-3xl font-default font-bold text-card-foreground">
                        {item.name}
                      </h3>
                      <p className="text-muted-foreground text-base leading-relaxed">
                        {item.description}
                      </p>
                    </div>

                    <div className="p-4 rounded-lg bg-secondary/60 border border-border/80">
                      <div className="text-xs font-semibold text-secondary-foreground uppercase tracking-wider mb-1">
                        Technical Specification
                      </div>
                      <div className="text-sm font-mono text-foreground font-medium">
                        {item.specs}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        <span>Direct coupling with NIR, Raman, and UV-Vis spectrophotometers</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        <span>Material test certificates and surface roughness reports (Ra &lt; 0.4 µm)</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        <span>Available in custom immersion depths and insertion angles</span>
                      </div>
                    </div>

                    <div className="pt-2 flex flex-wrap items-center gap-4">
                      <Button asChild size="default" className="font-semibold shadow-sm">
                        <Link to="/ContactPage">
                          Request Technical Data
                          <ArrowRight className="size-4 ml-1" />
                        </Link>
                      </Button>
                      <Button asChild variant="outline" size="default">
                        <Link to="/ProcessAnalyticalTechnologyPage">
                          Explore PAT Systems
                        </Link>
                      </Button>
                    </div>
                  </div>

                  <div className="lg:col-span-6">
                    <div className="relative aspect-4/3 w-full rounded-lg overflow-hidden bg-muted border border-border">
                      <img src={item.image} data-wvc-srcset={item.imageSrcset} data-wvc-sizes={item.imageSizes} alt={item.name} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                      <div className="absolute top-3 right-3 bg-background/90 backdrop-blur-xs border border-border px-3 py-1.5 rounded-md text-xs font-medium text-foreground flex items-center gap-1.5 shadow-xs">
                        <Gauge className="size-3.5 text-primary" />
                        Qualified Precision
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </Tabs>
        </div>

        {/* System Integration Grid */}
        <div className="mb-20">
          <div className="max-w-2xl mb-8">
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground mb-3">
              Process &amp; Laboratory Integration Capabilities
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              Bridge the gap between raw analytical telemetry and automated plant control networks through proven field bus and software integrations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {integrationFeatures.map((feat, i) => <motion.div key={feat.title} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.4,
            delay: i * 0.08
          }} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <Badge variant="secondary" className="text-[11px] font-medium">
                        {feat.tag}
                      </Badge>
                      <Network className="size-4 text-primary" />
                    </div>
                    <CardTitle className="text-lg font-default font-bold text-card-foreground">
                      {feat.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {feat.desc}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {/* FAQ & Support Banner */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 bg-secondary/50 border border-border rounded-xl p-6 sm:p-8 space-y-6">
            <div className="space-y-2">
              <Badge variant="default" className="bg-primary text-primary-foreground">
                Technical Guidance
              </Badge>
              <h3 className="text-xl sm:text-2xl font-default font-bold text-secondary-foreground">
                Need integration assistance on site?
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Antech Solutions engineers provide full dimensional surveying, fitting design, and control network commissioning across facilities in Ireland and the UK.
              </p>
            </div>

            <div className="space-y-3 border-t border-border/80 pt-4 text-sm text-secondary-foreground font-medium">
              <div className="flex items-center gap-2">
                <Settings className="size-4 text-primary" />
                <span>Custom bracketry &amp; hygienic flange machining</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary" />
                <span>On-site optical alignment &amp; pressure testing</span>
              </div>
            </div>

            <Button asChild size="default" className="w-full font-semibold shadow-xs">
              <Link to="/ContactPage">
                Speak with an Applications Specialist
              </Link>
            </Button>
          </div>

          <div className="lg:col-span-7 bg-card border border-border rounded-xl p-6 sm:p-8 shadow-xs">
            <h3 className="text-xl font-default font-bold text-card-foreground mb-4">
              Frequently Asked Integration Questions
            </h3>
            <Accordion type="single" collapsible className="w-full">
              {faqList.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-border">
                  <AccordionTrigger className="text-base font-semibold text-foreground text-left py-4 hover:no-underline hover:text-primary">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}